<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="800"
    scrollable
    persistent
  >
    <v-card class="preview-card">
      <!-- Header -->
      <v-card-title class="preview-header pa-4">
        <div class="d-flex align-center justify-space-between" style="width: 100%">
          <div class="d-flex align-center">
            <v-icon class="mr-3" color="white">mdi-eye</v-icon>
            <span class="preview-title">Pregled Ispisa</span>
          </div>
          <v-btn icon dark @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Paper Size Selector -->
      <div class="pa-4 d-flex align-center justify-center">
        <v-chip-group
          v-model="paperSize"
          active-class="primary--text"
          mandatory
        >
          <v-chip value="58">58mm</v-chip>
          <v-chip value="80">80mm</v-chip>
        </v-chip-group>
      </div>

      <v-divider></v-divider>

      <!-- Preview Content -->
      <v-card-text class="preview-content pa-6">
        <div class="preview-disclaimer mb-4">
          <v-alert type="info" text dense>
            <v-icon class="mr-2" small>mdi-information</v-icon>
            Ovo je približan prikaz. Koristitе "Testiraj Ispis" za točnu verifikaciju na fizičkom printeru.
          </v-alert>
        </div>

        <!-- Simulated Printer Paper -->
        <div class="printer-paper-wrapper">
          <div
            class="printer-paper"
            :class="`paper-${paperSize}mm`"
          >
            <!-- Header Section -->
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.header)"
            >
              STOL 5
            </div>
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.header)"
            >
              NARUDŽBA 123
            </div>

            <div class="separator">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>

            <!-- Metadata Section -->
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.metadata)"
            >
              Operater: Ivan Horvat
            </div>
            <div
              class="print-section print-section-row"
              :class="getSectionClasses(printFormat.metadata)"
            >
              <span>Datum: 21.01.2026</span>
              <span>Vrijeme: 14:35</span>
            </div>

            <div class="separator">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>

            <!-- Courses Section -->
            <div
              class="print-section"
              :class="getSectionClasses({ ...printFormat.courses, alignment: 1 })"
            >
              == PREDJELO ==
            </div>
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.courses)"
            >
              1 X BRUSCHETTA SA RAJČICOM
            </div>

            <div class="separator">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>

            <div
              class="print-section"
              :class="getSectionClasses({ ...printFormat.courses, alignment: 1 })"
            >
              == GLAVNO JELO ==
            </div>
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.courses)"
            >
              1 X ODREZAK SA POVRĆEM
            </div>

            <div class="separator">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>

            <!-- Items Section -->
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.items)"
            >
              2 X PIZZA MARGHERITA
            </div>
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.items)"
            >
              1 X SALATA CEZAR SA PILETINOM
            </div>
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.items)"
            >
              - Bez luka molim
            </div>

            <div class="separator">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>

            <!-- Menu Items Section -->
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.items)"
            >
              1 X DNEVNI MENI
            </div>
            <div
              class="print-section menu-indent"
              :class="getSectionClasses(printFormat.menuItems)"
            >
              JUHA
            </div>
            <div
              class="print-section menu-indent"
              :class="getSectionClasses(printFormat.menuItems)"
            >
              ∙ Goveđa juha
            </div>
            <div
              class="print-section menu-indent"
              :class="getSectionClasses(printFormat.menuItems)"
            >
              GLAVNO JELO
            </div>
            <div
              class="print-section menu-indent"
              :class="getSectionClasses(printFormat.menuItems)"
            >
              ∙ Pileći file sa rižom
            </div>

            <div class="separator">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>

            <!-- Footer Section -->
            <div
              class="print-section"
              :class="getSectionClasses(printFormat.footer)"
            >
              - Hitan stol - molim brzo
            </div>

            <div class="paper-end"></div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions -->
      <v-card-actions class="pa-4 justify-center">
        <v-btn
          color="primary"
          large
          @click="$emit('input', false)"
        >
          <v-icon left>mdi-check</v-icon>
          Zatvori
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'PrintPreviewDialog',

  props: {
    value: {
      type: Boolean,
      required: true
    },
    printFormat: {
      type: Object,
      required: true
    },
    printerWidth: {
      type: Number,
      default: 80
    }
  },

  data () {
    return {
      paperSize: '80' // Default to 80mm
    }
  },

  watch: {
    printerWidth: {
      immediate: true,
      handler (val) {
        this.paperSize = val === 58 ? '58' : '80'
      }
    }
  },

  methods: {
    /**
     * Generate CSS classes for a print section based on format settings
     */
    getSectionClasses (sectionFormat) {
      const classes = []

      // Font type
      classes.push(`font-${sectionFormat.fontType.toLowerCase()}`)

      // Font size
      classes.push(`size-${sectionFormat.fontSize}`)

      // Bold
      if (sectionFormat.bold) {
        classes.push('bold')
      }

      // Alignment
      if (sectionFormat.alignment === 1) {
        classes.push('align-center')
      } else if (sectionFormat.alignment === 2) {
        classes.push('align-right')
      } else {
        classes.push('align-left')
      }

      return classes.join(' ')
    }
  }
}
</script>

<style scoped>
.preview-card {
  background: rgba(255, 255, 255, 0.98) !important;
}

.preview-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.9) 0%,
    rgba(57, 137, 201, 0.85) 50%,
    rgba(97, 84, 164, 0.9) 100%) !important;
}

.preview-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: white !important;
}

.preview-content {
  background: #f5f5f5 !important;
}

.preview-disclaimer {
  max-width: 600px;
  margin: 0 auto;
}

.printer-paper-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.printer-paper {
  background: white;
  padding: 20px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  line-height: 1.4;
  position: relative;
}

.paper-58mm {
  width: 220px;
  max-width: 100%;
}

.paper-80mm {
  width: 300px;
  max-width: 100%;
}

.print-section {
  margin: 4px 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.print-section-row {
  display: flex;
  justify-content: space-between;
}

.menu-indent {
  padding-left: 16px;
}

.separator {
  margin: 8px 0;
  color: #666;
  overflow: hidden;
  text-overflow: clip;
}

.paper-end {
  height: 40px;
  border-top: 2px dashed #ccc;
  margin-top: 20px;
}

/* Font Types - with more distinct differences */
.font-a {
  font-family: 'Courier New', 'Courier', monospace;
  letter-spacing: 0;
}

.font-b {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.85em;
  letter-spacing: -0.5px;
}

.font-c {
  font-family: 'Monaco', 'Lucida Console', monospace;
  font-size: 0.75em;
  letter-spacing: -1px;
}

/* Font Sizes */
.size-0 {
  font-size: 1.0em;
  line-height: 1.4;
  margin-bottom: 2px;
}

.size-1 {
  font-size: 1.25em;
  line-height: 1.4;
  margin-bottom: 2px;
}

.size-2 {
  font-size: 1.5em;
  line-height: 1.4;
  margin-bottom: 2px;
}

.size-3 {
  font-size: 2.0em;
  line-height: 1.5;
  margin-bottom: 6px;
}

.size-4 {
  font-size: 2.5em;
  line-height: 1.5;
  margin-bottom: 6px;
}

/* Bold */
.bold {
  font-weight: bold;
}

/* Alignment */
.align-left {
  text-align: left;
}

.align-center {
  text-align: center;
}

.align-right {
  text-align: right;
}

@media (max-width: 600px) {
  .printer-paper {
    max-width: calc(100vw - 48px);
  }

  .paper-80mm {
    width: 100%;
  }

  .paper-58mm {
    width: 80%;
  }
}
</style>
